import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getISectionVO,
    saveISectionPage,
} from '@/services/auditService/auditProgramService';
import {
    I0_PAGE,
    I21_PAGE,
    I21Page,
    I31_PAGE,
    I31Page,
    I3_PAGE,
    I_PAGE_TITLES,
    I_SECTION_PAGES,
    IProgrammePage,
    ISectionPageCode,
} from '@/pages/components/audit-program/i-section/constants';
import I21SystemTestView from '@/pages/components/audit-program/i-section/I21SystemTestView';
import I31CountView from '@/pages/components/audit-program/i-section/I31CountView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type ISectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: ISectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function ISectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'I0',
}: ISectionViewProps) {
    const [pageKey, setPageKey] = useState<ISectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('I');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getISectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of I_SECTION_PAGES) {
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load I Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: ISectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (!dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveISectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const i0Content = (getContent('I0') ?? I0_PAGE) as IProgrammePage;
    const i21Content = (getContent('I2-1') ?? I21_PAGE) as I21Page;
    const i3Content = (getContent('I3') ?? I3_PAGE) as IProgrammePage;
    const i31Content = (getContent('I3-1') ?? I31_PAGE) as I31Page;

    const tabs = useMemo(() => I_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={I_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'I0' ? (
                <G0ProgrammeView
                    header={header}
                    page={i0Content}
                    refCode="I0"
                    values={pageData.I0 ?? {}}
                    onChange={(k, v) => handleFieldChange('I0', k, v)}
                />
            ) : code === 'I2-1' ? (
                <I21SystemTestView
                    header={header}
                    page={i21Content}
                    values={pageData['I2-1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('I2-1', k, v)}
                />
            ) : code === 'I3' ? (
                <G0ProgrammeView
                    header={header}
                    page={i3Content}
                    refCode="I3"
                    values={pageData.I3 ?? {}}
                    onChange={(k, v) => handleFieldChange('I3', k, v)}
                />
            ) : (
                <I31CountView
                    header={header}
                    page={i31Content}
                    values={pageData['I3-1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('I3-1', k, v)}
                />
            )
        ) : null,
    })), [header, pageData, handleFieldChange, i0Content, i21Content, i3Content, i31Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    I Section — Inventories
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={!dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as ISectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
